/**
 * Couche d'evenements e-commerce GA4 -> window.dataLayer (consommee par GTM /
 * Addingwell). Le theme n'en emettait aucun : tout le tunnel etait aveugle.
 *
 * Contraintes structurelles de ce theme, respectees ici :
 *
 *  - `pubsub.js` fait `Promise.all(subscribers.map(cb => cb(data)))`. Une
 *    exception SYNCHRONE dans un abonne remonte hors de `publish()` et casse
 *    la chaine de `product-form.js` : le tiroir panier ne se rendrait plus.
 *    Chaque abonne est donc integralement enveloppe dans un try/catch.
 *
 *  - `PUB_SUB_EVENTS` est un `const` de portee lexicale globale : il n'existe
 *    PAS sur `window`. On le reference par son nom nu, comme le fait deja
 *    `sections/cart-drawer-advanced.liquid`.
 *
 *  - Aucun script inline ne doit vivre dans `#CartDrawer` : le tiroir
 *    re-execute ses `<script>` inline a chaque rendu. D'ou cet asset externe,
 *    que ce mecanisme ignore (il saute les scripts portant un `src`).
 */
(function () {
  'use strict';

  // L'editeur de theme emet `shopify:section:load` a chaque reglage, et le
  // dedoublonnage par WeakSet est indexe par noeud DOM — que l'editeur
  // remplace. Sans cette garde, un `view_item_list` part a chaque mouvement de
  // curseur. Meme regle que `assets/cart-drawer-progress-bar.js:422`.
  if (window.Shopify && window.Shopify.designMode) return;

  var CONFIG = window.ThemeAnalytics || {};
  var CURRENCY = CONFIG.currency || 'EUR';
  var LIST_NAME = CONFIG.listName || CONFIG.template || 'page';

  /* ---------------------------------------------------------------- socle */

  function dataLayer() {
    window.dataLayer = window.dataLayer || [];
    return window.dataLayer;
  }

  /**
   * Le `ecommerce: null` prealable est obligatoire : sans lui GA4 fusionne les
   * objets `ecommerce` successifs et les `items` de l'evenement precedent
   * fuient dans le suivant.
   */
  function pushEcommerce(eventName, payload) {
    try {
      var queue = dataLayer();
      queue.push({ ecommerce: null });
      queue.push({ event: eventName, ecommerce: payload });
    } catch (error) {
      /* le tracking ne doit jamais interrompre le parcours d'achat */
    }
  }

  function toAmount(cents) {
    var value = Number(cents);
    return isFinite(value) ? Math.round(value) / 100 : 0;
  }

  /* ------------------------------------------------- mapping ligne -> item */

  /**
   * Les reponses de /cart/add.js et de /cart/change|update.js partagent la
   * meme forme de ligne : un seul mapper couvre les deux, ainsi que l'etat
   * initial serialise par `snippets/analytics-datalayer-init.liquid`.
   */
  function itemFromLine(line, index) {
    if (!line) return null;

    var item = {
      item_id: String(line.sku || line.variant_id || line.id || ''),
      item_name: line.product_title || line.title || '',
      item_brand: line.vendor || '',
      item_category: line.product_type || '',
      item_variant: line.variant_title || '',
      price: toAmount(line.final_price != null ? line.final_price : line.price),
      quantity: Number(line.quantity) || 1
    };

    if (typeof index === 'number') item.index = index;
    return item;
  }

  function itemWithQuantity(line, quantity) {
    var item = itemFromLine(line);
    if (item) item.quantity = quantity;
    return item;
  }

  function itemsValue(items) {
    var total = 0;
    for (var i = 0; i < items.length; i++) {
      total += (Number(items[i].price) || 0) * (Number(items[i].quantity) || 0);
    }
    return Math.round(total * 100) / 100;
  }

  function emitItems(eventName, items) {
    var clean = [];
    for (var i = 0; i < items.length; i++) {
      if (items[i]) clean.push(items[i]);
    }
    if (!clean.length) return;

    pushEcommerce(eventName, {
      currency: CURRENCY,
      value: itemsValue(clean),
      items: clean
    });
  }

  /* ------------------------------------------------- etat courant du panier */

  /**
   * `line.key` (variante + proprietes) et non `variant_id` : une meme variante
   * peut occuper deux lignes distinctes avec des proprietes differentes.
   */
  function lineKey(line) {
    return line.key || String(line.variant_id || line.id || '');
  }

  var cartLines = {};

  function indexLines(cart) {
    var map = {};
    var lines = cart && Array.isArray(cart.items) ? cart.items : [];

    for (var i = 0; i < lines.length; i++) {
      map[lineKey(lines[i])] = {
        quantity: Number(lines[i].quantity) || 0,
        line: lines[i]
      };
    }
    return map;
  }

  function has(object, key) {
    return Object.prototype.hasOwnProperty.call(object, key);
  }

  /** Diffe le panier recu contre l'etat connu et renvoie les deltas. */
  function diffAgainstSnapshot(cart) {
    var next = indexLines(cart);
    var added = [];
    var removed = [];
    var key;

    for (key in next) {
      if (!has(next, key)) continue;
      var before = has(cartLines, key) ? cartLines[key].quantity : 0;
      var gained = next[key].quantity - before;
      if (gained > 0) added.push(itemWithQuantity(next[key].line, gained));
    }

    for (key in cartLines) {
      if (!has(cartLines, key)) continue;
      var after = has(next, key) ? next[key].quantity : 0;
      var lost = cartLines[key].quantity - after;
      if (lost > 0) removed.push(itemWithQuantity(cartLines[key].line, lost));
    }

    cartLines = next;
    return { added: added, removed: removed };
  }

  /**
   * Ajout via /cart/add.js. La reponse decrit la ligne resultante, pas le
   * delta : on soustrait la quantite connue. Le repli `delta <= 0` garde le
   * calcul juste si Shopify renvoyait un jour un delta.
   */
  function emitAddition(lines) {
    var items = [];

    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      var key = lineKey(line);
      var before = has(cartLines, key) ? cartLines[key].quantity : 0;
      var reported = Number(line.quantity) || 0;
      var delta = reported - before;

      if (delta <= 0) delta = reported;

      items.push(itemWithQuantity(line, delta));
      cartLines[key] = { quantity: Math.max(reported, before + delta), line: line };
    }

    emitItems('add_to_cart', items);
  }

  function currentCartItems() {
    var items = [];

    for (var key in cartLines) {
      if (!has(cartLines, key)) continue;
      items.push(itemWithQuantity(cartLines[key].line, cartLines[key].quantity));
    }
    return items;
  }

  cartLines = indexLines(CONFIG.cart);

  /* -------------------------------------- add_to_cart / remove_from_cart */

  function onCartUpdate(payload) {
    try {
      var data = payload && payload.cartData;
      if (!data) return;

      // Panier complet (/cart/change.js, /cart/update.js) : seul `item_count`
      // distingue de maniere fiable cette forme d'un ajout multiligne.
      if (Array.isArray(data.items) && data.item_count !== undefined) {
        var diff = diffAgainstSnapshot(data);
        emitItems('add_to_cart', diff.added);
        emitItems('remove_from_cart', diff.removed);
        return;
      }

      if (Array.isArray(data.items)) {
        emitAddition(data.items);
        return;
      }

      emitAddition([data]);
    } catch (error) {
      /* voir l'en-tete : une exception ici casserait le rendu du tiroir */
    }
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
  }

  // Les complements et cadeaux de variante sont POSTes par un fetch autonome
  // (blocks/product-buy-buttons-advanced.liquid, product-sticky-atc-advanced)
  // qui ne publie pas `cartUpdate`. Sans ce pont, ces lignes manquaient a
  // `add_to_cart`, et faussaient ensuite `view_cart` et `begin_checkout` en
  // laissant deriver le snapshot.
  document.addEventListener('theme:cart-lines-added', function (event) {
    try {
      var response = event.detail && event.detail.response;
      var lines = response && Array.isArray(response.items) ? response.items : [];

      emitAddition(lines);
    } catch (error) {
      /* la mesure ne doit jamais interrompre le parcours d'achat */
    }
  });

  /* ------------------------------------------------------------ view_cart */

  function emitViewCart() {
    emitItems('view_cart', currentCartItems());
  }

  /**
   * Le tiroir remplace la page /cart : sans ce crochet le tunnel n'a aucune
   * etape entre la fiche produit et le checkout.
   *
   * `CartDrawer.open()` sort tot quand le tiroir est deja `active` — on teste
   * l'etat AVANT d'appeler l'original, sinon chaque re-rendu gonflerait le
   * compteur.
   */
  var drawerIsOpen = false;

  function patchCartDrawer() {
    try {
      var CartDrawerClass = window.customElements && window.customElements.get('cart-drawer');
      if (!CartDrawerClass || !CartDrawerClass.prototype) return;
      if (CartDrawerClass.prototype.__dlViewCartPatched) return;

      var openOriginal = CartDrawerClass.prototype.open;
      var closeOriginal = CartDrawerClass.prototype.close;
      if (typeof openOriginal !== 'function') return;

      CartDrawerClass.prototype.__dlViewCartPatched = true;

      CartDrawerClass.prototype.open = function () {
        // `open()` pose la classe `active` dans un `setTimeout` : la lire ici
        // renverrait encore `false` sur un second appel rapproche, et emettrait
        // un doublon. On suit donc l'etat nous-memes, de maniere synchrone.
        var wasOpen = drawerIsOpen || this.classList.contains('active');
        drawerIsOpen = true;

        var result = openOriginal.apply(this, arguments);
        if (!wasOpen) emitViewCart();
        return result;
      };

      if (typeof closeOriginal === 'function') {
        CartDrawerClass.prototype.close = function () {
          drawerIsOpen = false;
          return closeOriginal.apply(this, arguments);
        };
      }
    } catch (error) {
      /* pas de tiroir : le theme reste intact */
    }
  }

  if (window.customElements && typeof window.customElements.whenDefined === 'function') {
    window.customElements.whenDefined('cart-drawer').then(patchCartDrawer);
  }

  if (CONFIG.pageType === 'cart') emitViewCart();

  /* -------------------------------------------------------- begin_checkout */

  var lastCheckoutPush = 0;

  /**
   * Navigation sortante : le POST natif quitte la page. On declenche au clic
   * (capture) plutot qu'au submit, pour gagner les quelques millisecondes qui
   * separent les deux. Jamais de preventDefault : la resoumission differee
   * casserait le transport natif du formulaire du panier.
   *
   * Cote GTM, configurer le tag GA4 avec `transport_type: 'beacon'` — sans
   * quoi le hit reste perdable au dechargement, quel que soit ce code.
   */
  function emitBeginCheckout() {
    var now = Date.now();
    if (now - lastCheckoutPush < 1500) return;
    lastCheckoutPush = now;

    emitItems('begin_checkout', currentCartItems());
  }

  document.addEventListener('click', function (event) {
    try {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var button = target.closest('#CartDrawer-Checkout, #checkout, button[name="checkout"], input[name="checkout"]');
      if (!button || button.disabled) return;

      emitBeginCheckout();
    } catch (error) {
      /* ne jamais bloquer le clic de paiement */
    }
  }, true);

  // Filet pour la soumission au clavier et les soumissions programmatiques,
  // ou aucun clic n'est emis.
  document.addEventListener('submit', function (event) {
    try {
      var form = event.target;
      if (!form || (form.id !== 'CartDrawer-Form' && form.id !== 'cart')) return;
      if (!event.submitter || event.submitter.name !== 'checkout') return;

      emitBeginCheckout();
    } catch (error) {
      /* ne jamais bloquer la soumission */
    }
  }, true);

  /* ------------------------------------------ view_item_list / select_item */

  var ATTRIBUTE = 'data-analytics-item';
  var seen = typeof WeakSet === 'function' ? new WeakSet() : null;
  var pending = {};
  var flushTimer = null;

  function readItem(element) {
    try {
      return JSON.parse(element.getAttribute(ATTRIBUTE));
    } catch (error) {
      return null;
    }
  }

  /** Les cartes produit apparaissent dans plusieurs sections d'une meme page. */
  function listIdFor(element) {
    var section = element.closest('[id^="shopify-section-"]');
    return section ? section.id : 'page';
  }

  function flush() {
    flushTimer = null;

    for (var listId in pending) {
      if (!has(pending, listId)) continue;
      var items = pending[listId];
      delete pending[listId];
      if (!items.length) continue;

      pushEcommerce('view_item_list', {
        currency: CURRENCY,
        item_list_id: listId,
        item_list_name: LIST_NAME,
        items: items
      });
    }
  }

  function queue(element) {
    var item = readItem(element);
    if (!item) return;

    var listId = listIdFor(element);
    if (!pending[listId]) pending[listId] = [];
    pending[listId].push(item);

    // Regroupe les cartes qui entrent ensemble dans le viewport en un seul
    // evenement, au lieu d'un par carte.
    if (flushTimer === null) flushTimer = setTimeout(flush, 300);
  }

  var observer = typeof IntersectionObserver === 'function'
    ? new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          // Grille en attente du filtre « Collection » (assets/collection-filter.js) :
          // ces cartes, masquees, vont etre remplacees par la liste filtree.
          if (entries[i].target.closest('.collection--filtering')) continue;
          observer.unobserve(entries[i].target);
          queue(entries[i].target);
        }
      }, { threshold: 0.3 })
    : null;

  function scan() {
    try {
      var cards = document.querySelectorAll('[' + ATTRIBUTE + ']');

      for (var i = 0; i < cards.length; i++) {
        if (seen && seen.has(cards[i])) continue;
        if (seen) seen.add(cards[i]);

        if (observer) observer.observe(cards[i]);
        else queue(cards[i]);
      }
    } catch (error) {
      /* liste non instrumentee : le reste de la page fonctionne */
    }
  }

  document.addEventListener('click', function (event) {
    try {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var card = target.closest('[' + ATTRIBUTE + ']');
      if (!card) return;

      var item = readItem(card);
      if (!item) return;

      pushEcommerce('select_item', {
        item_list_id: listIdFor(card),
        item_list_name: LIST_NAME,
        items: [item]
      });
    } catch (error) {
      /* ne jamais bloquer la navigation vers la fiche produit */
    }
  }, true);

  scan();

  // Le tiroir (upsells) et l'editeur de theme injectent des cartes apres coup.
  document.addEventListener('shopify:section:load', scan);
  // Le filtre « Collection » reconstruit la grille avec d'autres cartes.
  document.addEventListener('collection-filter:rendered', scan);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      try {
        scan();
      } catch (error) {
        /* idem : ne jamais remonter dans publish() */
      }
    });
  }
})();
